/**
 * useNotifications Hook
 *
 * React hook for managing user notifications
 */

import { useState, useEffect, useCallback, useRef } from 'react'

import { alertsApi, type UserNotification } from '@platform/alerts/api/alerts.api'
import { useTranslation } from 'react-i18next'

import { toBackendMessage } from '@/shared/utils/api'
import { apiErrorDetails } from '@/shared/utils/api-error-details'
import { logger } from '@/shared/utils/logger'

/** Why the list could not be loaded or refreshed. */
interface NotificationsLoadError {
  /** `CODE · requestId` of the failed request (`apiErrorDetails`); undefined when it carried neither. */
  details: string | undefined
}

/**
 * What the bell knows about the unread count:
 * - `unknown`: no answer yet;
 * - `loaded`: the last request answered;
 * - `stale`: it answered once and a later request failed: the number is the last good one;
 * - `unavailable`: it never answered and the last request failed: there is no number to show.
 */
type UnreadCountStatus = 'unknown' | 'loaded' | 'stale' | 'unavailable'

export interface UseNotificationsResult {
  notifications: UserNotification[]
  unreadCount: number
  loading: boolean
  error: string | null
  /** The last LIST request failed; cleared by the next one that succeeds. `error` carries the failures of the actions (a list load still clears it when it starts). */
  loadError: NotificationsLoadError | null
  /** What is known about `unreadCount`: see `UnreadCountStatus`. */
  unreadCountStatus: UnreadCountStatus
  fetchNotifications: () => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markAsRead: (id: string) => Promise<void>
  // Resolves true only when the backend recorded the acknowledgement — callers
  // (e.g. NotificationsPanel) gate follow-up navigation on this result.
  acknowledge: (id: string) => Promise<boolean>
  markAllAsRead: () => Promise<void>
  deleteNotification: (id: string) => Promise<void>
  deleteReadNotifications: () => Promise<void>
  refresh: () => Promise<void>
}

/**
 * `alertsApi` throws this for a 2xx whose body is not what the notifications API promises. Must equal
 * the code of `invalidNotificationsResponse` in alerts.api.ts (not imported: the hook tests replace
 * that module with `alertsApi` alone).
 */
const UNCONFIRMED_ACTION_CODE = 'ALERTS_INVALID_RESPONSE'

/**
 * Whether the failure says the local list can no longer be trusted: the target is gone (a 404: the
 * notification was deleted from another tab) or the server may have applied the action without us
 * knowing (an unreadable answer). By shape, not `instanceof`, so it does not depend on which
 * `ApiException` class built the error.
 */
function isStaleTargetError(err: unknown): boolean {
  return errorStatus(err) === 404 || errorCode(err) === UNCONFIRMED_ACTION_CODE
}

/** The `code` of a rejection, read by shape (see `isStaleTargetError`). */
function errorCode(err: unknown): string | undefined {
  if (typeof err === 'object' && err !== null && 'code' in err && typeof err.code === 'string') {
    return err.code
  }
  return undefined
}

/** The HTTP `status` of a rejection, read by shape (see `isStaleTargetError`). */
function errorStatus(err: unknown): number | undefined {
  if (
    typeof err === 'object' &&
    err !== null &&
    'status' in err &&
    typeof err.status === 'number'
  ) {
    return err.status
  }
  return undefined
}

export function useNotifications(recentOnly = true): UseNotificationsResult {
  const { t } = useTranslation('errors')
  // `fetchNotifications` must never depend on `t`: react-i18next re-emits it when a catalogue
  // arrives and on every language change, and through `refresh` that re-ran the initial load in
  // a self-feeding loop (measured: 67 calls per function after five renders against 1;
  // useNotifications.unstable-t-loop.test.tsx).
  // Since #4002 its catch translates nothing (it keeps the code and the request id), so `t` is
  // read only by the action handlers below, which no effect triggers.
  const [notifications, setNotifications] = useState<UserNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<NotificationsLoadError | null>(null)
  // Once a count has loaded, a failed poll keeps the last count and marks it 'stale'; the next
  // good answer makes it 'loaded' again. Never loaded and failing is 'unavailable'.
  const [unreadCountStatus, setUnreadCountStatus] = useState<UnreadCountStatus>('unknown')
  // One log line per run of failures, not one per poll (the poll is every 30 s, per open tab).
  const unreadCountFailingRef = useRef(false)

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const data = await alertsApi.getRecentNotifications(recentOnly ? 10 : 50)
      setNotifications(data)
      setLoadError(null)
    } catch (err) {
      // Keep the rows already on screen: a failed refresh must not look like an empty inbox.
      logger.error('useNotifications.fetchNotifications failed', err)
      setLoadError({ details: apiErrorDetails(err) })
    } finally {
      setLoading(false)
    }
  }, [recentOnly])

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await alertsApi.getUnreadCount()
      setUnreadCount(count)
      setUnreadCountStatus('loaded')
      unreadCountFailingRef.current = false
    } catch (error) {
      // Keep the last known count: one failed poll must not switch the badge off.
      if (!unreadCountFailingRef.current) {
        logger.error('useNotifications.fetchUnreadCount failed', error)
      }
      unreadCountFailingRef.current = true
      setUnreadCountStatus((status) =>
        status === 'loaded' || status === 'stale' ? 'stale' : 'unavailable',
      )
    }
  }, [])

  const refresh = useCallback(async () => {
    await Promise.all([fetchNotifications(), fetchUnreadCount()])
  }, [fetchNotifications, fetchUnreadCount])

  // Every failed action ends here: the banner says why the server refused (its localized message),
  // or that the outcome could not be confirmed. After a 404 or an unreadable answer the list is
  // read again FIRST (`fetchNotifications` starts by clearing the banner, so the banner goes after).
  // That read can fail: it never throws, it sets `loadError` and keeps the rows on screen, so the
  // action banner is shown either way. No local state was touched before the answer: nothing to undo.
  const failAction = useCallback(
    async (err: unknown) => {
      logger.error('useNotifications action failed', err)
      const message =
        errorCode(err) === UNCONFIRMED_ACTION_CODE
          ? t(
              'alerts.notification_action_unconfirmed',
              'Could not confirm the notification action. Check the list.',
            )
          : toBackendMessage(err, t)
      if (isStaleTargetError(err)) await refresh()
      setError(message)
    },
    [t, refresh],
  )

  const markAsRead = useCallback(
    async (id: string) => {
      try {
        await alertsApi.markAsRead(id)
        setError(null)
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date() } : n)),
        )
        setUnreadCount((prev) => Math.max(0, prev - 1))
      } catch (err) {
        await failAction(err)
      }
    },
    [failAction],
  )

  const acknowledge = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await alertsApi.acknowledge(id)
        setError(null)
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === id
              ? { ...n, isRead: true, isAcknowledged: true, acknowledgedAt: new Date() }
              : n,
          ),
        )
        setUnreadCount((prev) => Math.max(0, prev - 1))
        return true
      } catch (err) {
        await failAction(err)
        return false
      }
    },
    [failAction],
  )

  const markAllAsRead = useCallback(async () => {
    try {
      // 0 marked is a success (another tab may have marked them already).
      await alertsApi.markAllAsRead()
      setError(null)
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true, readAt: new Date() })))
      // The server has just said none is left unread: the count is known again, not stale.
      setUnreadCount(0)
      setUnreadCountStatus('loaded')
      unreadCountFailingRef.current = false
    } catch (err) {
      await failAction(err)
    }
  }, [failAction])

  const deleteNotification = useCallback(
    async (id: string) => {
      try {
        await alertsApi.deleteNotification(id)
        setError(null)
        // Decide "was the removed row unread?" ONCE, at the top level, BEFORE
        // queueing the updates. Deciding it INSIDE the setNotifications updater
        // made the bell badge drop by TWO per delete: main.tsx mounts the app in
        // <React.StrictMode>, which double-renders and re-processes the pending
        // update queue, so the outer updater ran twice and queued the RELATIVE
        // decrement twice. Same shape as markAsRead/acknowledge above, which
        // already call setUnreadCount at the top level.
        //
        // unreadCount is the SERVER-wide total (fetchUnreadCount), not the number
        // of unread rows in this fetched page, so it cannot be recomputed from the
        // list — the update has to stay relative, and therefore has to fire once.
        const removedNotificationWasUnread = notifications.some((n) => n.id === id && !n.isRead)
        setNotifications((prev) => prev.filter((n) => n.id !== id))
        if (removedNotificationWasUnread) {
          setUnreadCount((current) => Math.max(0, current - 1))
        }
      } catch (err) {
        await failAction(err)
      }
    },
    [notifications, failAction],
  )

  const deleteReadNotifications = useCallback(async () => {
    try {
      // 0 deleted is a success (there was nothing read to delete).
      await alertsApi.deleteReadNotifications()
      setError(null)
      setNotifications((prev) => prev.filter((n) => !n.isRead))
    } catch (err) {
      await failAction(err)
    }
  }, [failAction])

  // Initial fetch
  useEffect(() => {
    refresh()
  }, [refresh])

  // Poll the unread count every 30 seconds (the bell badge is its only reader)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUnreadCount()
    }, 30000)

    return () => clearInterval(interval)
  }, [fetchUnreadCount])

  return {
    notifications,
    unreadCount,
    loading,
    error,
    loadError,
    unreadCountStatus,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    acknowledge,
    markAllAsRead,
    deleteNotification,
    deleteReadNotifications,
    refresh,
  }
}
